import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Tooltip} from '@/components/Tooltip';
import {Typography} from '@/components/Typography';
import {useGA4WithParams} from '@/hooks/tracking';
import {DayPopulation, FloatingPopulation} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {Flexbox} from '@/styled-components';
import {palette} from '@/styles';
import {
  GrayCenterBox, HighlightBox, SectionBlock, SectionSubTitle, SectionTab, SectionTabs, SectionTitle
} from '../../StyledComponents';
import {TimeTrafficSynchronizedAreaChart} from '../Chart/TimeTrafficSynchronizedAreaChart';
import {EMPTY_ITEM_TYPES, POPULATION_TIME_DAY, POPULATION_TIME_HOUR} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {PopulationDataProps} from '../interface';
import {processFootTrafficData} from '../utils';

export const FootTrafficCompare = ({
  isPending,
  error,
  data: rawdata,
}: PopulationDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () => dataSource?.find((source) => source.key === 'floatingPopulation'),
    [dataSource]
  );

  const data = useMemo(() => {
    return processFootTrafficData(rawdata?.map((i) => i.floatingPopulation));
  }, [rawdata]);

  return (
    <Root>
      <div>
        <Flexbox>
          <SectionTitle>유동 인구</SectionTitle>
          <Tooltip
            openOnClick
            closeEvents={{
              mouseout: false,
              blur: false,
              click: true,
            }}
            iconStyle={{marginLeft: 2}}
            render={() => (
              <div>
                오전 : 6:00 ~ 10:59 | 점심 : 11:00 ~ 14:59 |<br />
                오후 : 15:00 ~ 17:59 | 저녁 : 18:00 ~ 20:59 |<br />
                밤 : 21:00 ~ 00:59
                <br />
                (새벽 시간대는 유동량이 많지 않아 미제공)
              </div>
            )}
          />
        </Flexbox>
        <SectionSubTitle>
          {`${metadata?.source || '마이프차'}∙${metadata?.baseDate || ''}∙명`}
        </SectionSubTitle>
      </div>
      {isPending ? (
        <GrayCenterBox>
          <LoadingBar color={palette.textSecondary} />
        </GrayCenterBox>
      ) : error ? (
        <Typography variant="medium12" color={palette.textSecondary}>
          분석 실패
        </Typography>
      ) : !data ? (
        <EmptyItem type={EMPTY_ITEM_TYPES.Population} />
      ) : (
        <TimeTrafficSynchronizedAreaChartBox items={data} />
      )}
    </Root>
  );
};

const TimeTrafficSynchronizedAreaChartBox = ({
  items,
}: {
  items: Array<FloatingPopulation & {populationTotal?: DayPopulation[]}>;
}) => {
  const tracking = useGA4WithParams();
  const [tabIndex, setTabIndex] = useState<number>(0);

  const compareList = items.map((_, idx) => `비교 상권 ${idx + 1}`);

  const maxData = items
    .map((item) => {
      const {day, week, populationTotal} = item;
      if (
        !populationTotal ||
        populationTotal.length === 0 ||
        !day ||
        day.length === 0 ||
        !week ||
        week.length === 0
      ) {
        return null;
      }
      const dataArr = [populationTotal, day, week];
      const max = dataArr[tabIndex].reduce(
        (prev: any, curr: any) => {
          return prev.totalPopulation > curr.totalPopulation ? prev : curr;
        },
        {totalPopulation: 0, hour: 0}
      );
      return max;
    })
    .filter((item) => item !== null);

  const maxIndexAndHour = maxData.findIndex((item) => {
    return (
      item.totalPopulation ===
      Math.max(...maxData.map((data) => data.totalPopulation))
    );
  });

  return (
    <>
      <SectionTabs>
        {POPULATION_TIME_DAY.map((value, index) => (
          <SectionTab
            key={`${value}-${index}`}
            active={index === tabIndex}
            onClick={() => {
              tracking('analysis_compare_floatperson', {
                time_info: value,
              });
              setTabIndex(index);
            }}
          >
            {value}
          </SectionTab>
        ))}
      </SectionTabs>

      {items.map((item, index) => {
        return (
          <TimeTrafficSynchronizedAreaChart
            key={`유동인구그래프-${index}`}
            type={'유동 인구'}
            data={item}
            compareItemName={compareList[index]}
            compareItemIndex={index}
            tabIndex={tabIndex}
          />
        );
      })}

      <HighlightBox>
        <Typography variant="medium12">
          {!!maxData.length ? (
            <>
              <span>
                <Typography color={palette.primary500} fontWeight="700">
                  {compareList[maxIndexAndHour]}
                </Typography>
              </span>
              의{' '}
              <strong>
                {
                  POPULATION_TIME_HOUR.find(
                    (v) => v.time === maxData[maxIndexAndHour].hour
                  )?.name
                }{' '}
                시간대
              </strong>{' '}
              유동인구가 가장 많아요.
            </>
          ) : (
            `선택하신 지역에 유동인구 데이터가 없습니다.`
          )}
        </Typography>
      </HighlightBox>
    </>
  );
};

const Root = styled(SectionBlock)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
